import { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  CheckCircle, XCircle, Loader2, ChevronRight,
  ArrowRight, Ban, Shield, AlertOctagon,
  Newspaper, Building2, Scale, Wifi, Banknote, Users,
  FileText, Download, Briefcase, MapPin, UserCheck,
  RotateCcw, X as XIcon,
} from 'lucide-react';
import { useEDDPoller } from '@/hooks/profiler/useEDDPoller';
import { RiskTierBadge } from '@/components/profiler/RiskTierBadge';
import { PivotChain } from '@/components/profiler/PivotChain';
import { IdentifierClusterGrid } from '@/components/profiler/IdentifierClusterGrid';
import { MODULE_LABELS, TIER_COLOR, SEED_LABELS } from '@/types/profiler';
import type { CheckResult, ModuleStatus, RiskTier, CDDReport, AdverseArticle, EnforcementHit, CourtCase, SocialAccount, CommerceFootprint, LinkedInProfile } from '@/types/profiler';
import { profilerApi } from '@/api/profiler';
import { useAuth } from '@/contexts/AuthContext';

// ── Pipeline module order ─────────────────────────────────────────────────────

const PIPELINE_ORDER = [
  'identity', 'sanctions', 'pep', 'adverse_media',
  'enforcement', 'ato', 'social', 'corporate', 'wealth', 'court',
];

const MODULE_ICONS: Record<string, typeof Shield> = {
  identity:     Shield,
  sanctions:    AlertOctagon,
  pep:          Users,
  adverse_media: Newspaper,
  enforcement:  Scale,
  ato:          Wifi,
  social:       Users,
  corporate:    Building2,
  wealth:       Banknote,
  court:        Scale,
};

const MODULE_DESCRIPTIONS: Record<string, string> = {
  identity:     'Verifying government-issued IDs, PAN, DL, Voter ID',
  sanctions:    'Screening OpenSanctions, OFAC, UN, EU lists',
  pep:          'Checking PEP registries & UBO ownership chains',
  adverse_media:'NLP classification of news & press articles',
  enforcement:  'SEBI, RBI, ED, NCLT enforcement orders',
  ato:          'Stealer logs & breach exposure analysis',
  social:       'Cross-platform social account discovery',
  corporate:    'MCA21 directorship, SEBI, ICAI checks',
  wealth:       'UPI handles, vehicles, property, credit signals',
  court:        'eCourts Supreme & High Court case lookup',
};

const TOTAL_MODULES = PIPELINE_ORDER.length;

// ── Helpers ───────────────────────────────────────────────────────────────────

// AMLModule enum values are uppercase ("INDIA_IDENTITY") — normalize to frontend keys
function normalizeModule(raw: string): string {
  const lower = raw.toLowerCase();
  if (lower === 'india_identity') return 'identity';
  if (lower === 'india_corporate') return 'corporate';
  return lower;
}

function useElapsedSeconds(startIso?: string): number {
  const [elapsed, setElapsed] = useState(0);
  const ref = useRef<ReturnType<typeof setInterval> | null>(null);
  useEffect(() => {
    if (!startIso) return;
    const tick = () => setElapsed(Math.floor((Date.now() - new Date(startIso!).getTime()) / 1000));
    tick();
    ref.current = setInterval(tick, 1000);
    return () => { if (ref.current) clearInterval(ref.current); };
  }, [startIso]);
  return elapsed;
}

function fmt(s: number) {
  const m = Math.floor(s / 60);
  return m === 0 ? `${s}s` : `${m}m ${(s % 60).toString().padStart(2, '0')}s`;
}

function resolveStatus(module: string, crs?: CheckResult[]): ModuleStatus | 'QUEUED' {
  const cr = crs?.find(r => normalizeModule(r.module) === module);
  if (!cr) return 'QUEUED';
  // AMLCheckResult has no status field — presence of a record means complete
  return cr.status ?? 'COMPLETE';
}

function countCompleted(crs?: CheckResult[]) {
  // AMLCheckResult records are only written after module completes — presence = done
  if (!crs) return 0;
  return crs.filter(r =>
    !r.status || r.status === 'COMPLETE' || r.status === 'DEGRADED' || r.status === 'FAILED'
  ).length;
}

function activeModule(crs?: CheckResult[]): string | null {
  return crs?.find(r => r.status === 'RUNNING')?.module ?? null;
}

// ── Cinematic scan loader ─────────────────────────────────────────────────────

function ScanLoader({
  entityName,
  elapsed,
  completedCount,
  total,
  currentModule,
  discoveries,
}: {
  entityName: string;
  elapsed: number;
  completedCount: number;
  total: number;
  currentModule: string | null;
  discoveries: number;
}) {
  const pct = Math.round((completedCount / total) * 100);
  const moduleName = currentModule ? (MODULE_LABELS[currentModule] ?? currentModule) : 'Initialising…';
  const moduleDesc = currentModule ? (MODULE_DESCRIPTIONS[currentModule] ?? '') : 'Building entity cluster';

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        background: '#050709',
        gap: 0,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Background radial gradient */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background: 'radial-gradient(ellipse 60% 50% at 50% 50%, #001A2C 0%, #050709 70%)',
          pointerEvents: 'none',
        }}
      />

      {/* Scanner SVG */}
      <div style={{ position: 'relative', width: 320, height: 320, marginBottom: 32 }}>
        <svg width="320" height="320" viewBox="0 0 320 320" style={{ position: 'absolute', inset: 0 }}>
          {/* Static rings */}
          {[60, 100, 140].map((r, i) => (
            <circle key={r} cx={160} cy={160} r={r}
              fill="none" stroke="#182030"
              strokeWidth={i === 2 ? 1.5 : 0.8}
              strokeDasharray={i === 1 ? '4 6' : undefined}
            />
          ))}

          {/* Animated rotating scan arc */}
          <g style={{ transformOrigin: '160px 160px' }}>
            <animateTransform
              attributeName="transform" type="rotate"
              values="0 160 160;360 160 160" dur="3s" repeatCount="indefinite"
            />
            <path
              d="M 160 160 L 160 20 A 140 140 0 0 1 260 160 Z"
              fill="url(#scanGrad)"
              opacity={0.22}
            />
            {/* Leading edge */}
            <line x1="160" y1="160" x2="160" y2="20"
              stroke="#00C8FF" strokeWidth={1.5} opacity={0.7}
            />
          </g>

          {/* Pulsing centre node */}
          <circle cx={160} cy={160} r={28} fill="#050709" stroke="#00C8FF" strokeWidth={2} />
          <circle cx={160} cy={160} r={28} fill="none" stroke="#00C8FF" strokeWidth={1} opacity={0.3}>
            <animate attributeName="r" values="28;38;28" dur="2s" repeatCount="indefinite" />
            <animate attributeName="opacity" values="0.3;0;0.3" dur="2s" repeatCount="indefinite" />
          </circle>

          {/* Module dots on outer ring */}
          {PIPELINE_ORDER.map((mod, i) => {
            const angle = (i / PIPELINE_ORDER.length) * 360 - 90;
            const rad = angle * (Math.PI / 180);
            const x = 160 + 140 * Math.cos(rad);
            const y = 160 + 140 * Math.sin(rad);
            const status = mod === currentModule ? 'RUNNING'
              : i < completedCount ? 'COMPLETE'
              : 'QUEUED';
            const color = status === 'COMPLETE' ? '#00C853'
              : status === 'RUNNING' ? '#F0B429'
              : '#182030';
            return (
              <g key={mod}>
                <circle cx={x} cy={y} r={7} fill={color} opacity={status === 'QUEUED' ? 0.4 : 1}>
                  {status === 'RUNNING' && (
                    <>
                      <animate attributeName="r" values="7;11;7" dur="1.2s" repeatCount="indefinite" />
                      <animate attributeName="opacity" values="1;0.3;1" dur="1.2s" repeatCount="indefinite" />
                    </>
                  )}
                </circle>
                {status === 'RUNNING' && (
                  <circle cx={x} cy={y} r={7} fill="none" stroke="#F0B429" strokeWidth={1.5} opacity={0.5}>
                    <animate attributeName="r" values="7;14;7" dur="1.2s" repeatCount="indefinite" />
                    <animate attributeName="opacity" values="0.5;0;0.5" dur="1.2s" repeatCount="indefinite" />
                  </circle>
                )}
              </g>
            );
          })}

          {/* Gradient definition */}
          <defs>
            <radialGradient id="scanGrad" cx="0%" cy="100%" r="100%">
              <stop offset="0%" stopColor="#00C8FF" stopOpacity="0" />
              <stop offset="100%" stopColor="#00C8FF" stopOpacity="0.5" />
            </radialGradient>
          </defs>
        </svg>

        {/* Centre text */}
        <div style={{
          position: 'absolute', inset: 0,
          display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center',
          gap: 2, pointerEvents: 'none',
        }}>
          <span style={{ color: '#00C8FF', fontFamily: 'monospace', fontWeight: 900, fontSize: 18, lineHeight: 1 }}>
            {pct}%
          </span>
          <span style={{ color: '#5A7A9A', fontFamily: 'monospace', fontSize: 8 }}>
            SCANNED
          </span>
        </div>
      </div>

      {/* Entity name */}
      <p style={{ color: '#DCE8F5', fontFamily: 'monospace', fontWeight: 700, fontSize: 16, letterSpacing: '0.1em', marginBottom: 6 }}>
        {entityName || 'ENTITY'}
      </p>

      {/* Active module ticker */}
      <AnimatePresence mode="wait">
        <motion.div
          key={moduleName}
          initial={{ opacity: 0, y: 4 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -4 }}
          transition={{ duration: 0.25 }}
          style={{ textAlign: 'center', marginBottom: 32 }}
        >
          <p style={{ color: '#F0B429', fontFamily: 'monospace', fontSize: 11, fontWeight: 700, letterSpacing: '0.12em' }}>
            {moduleName.toUpperCase()}
          </p>
          <p style={{ color: '#3A5A7A', fontFamily: 'monospace', fontSize: 9, marginTop: 3 }}>
            {moduleDesc}
          </p>
        </motion.div>
      </AnimatePresence>

      {/* Progress bar */}
      <div style={{ width: 320, marginBottom: 20 }}>
        <div style={{ height: 2, background: '#182030', borderRadius: 2, overflow: 'hidden' }}>
          <motion.div
            animate={{ width: `${pct}%` }}
            transition={{ duration: 0.8, ease: 'easeOut' }}
            style={{ height: '100%', background: 'linear-gradient(90deg, #00C8FF, #2979FF)', borderRadius: 2 }}
          />
        </div>
      </div>

      {/* Stats row */}
      <div style={{ display: 'flex', gap: 28 }}>
        {[
          { label: 'MODULES', value: `${completedCount}/${total}` },
          { label: 'ELAPSED', value: fmt(elapsed) },
          { label: 'IDENTIFIERS', value: String(discoveries) },
        ].map(({ label, value }) => (
          <div key={label} style={{ textAlign: 'center' }}>
            <p style={{ color: '#00C8FF', fontFamily: 'monospace', fontWeight: 800, fontSize: 14 }}>{value}</p>
            <p style={{ color: '#3A5A7A', fontFamily: 'monospace', fontSize: 8, marginTop: 2 }}>{label}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Module row (compact) ──────────────────────────────────────────────────────

function ModuleRow({ module, status, summary }: {
  module: string;
  status: ModuleStatus | 'QUEUED';
  summary?: string;
}) {
  const label = MODULE_LABELS[module] ?? module;
  const Icon = MODULE_ICONS[module] ?? Shield;

  const color =
    status === 'COMPLETE'  ? '#00C853'
    : status === 'RUNNING' ? '#F0B429'
    : status === 'FAILED'  ? '#FF1F1F'
    : status === 'DEGRADED'? '#F0B429'
    : '#283A50';

  return (
    <div
      style={{
        display: 'flex', alignItems: 'center', gap: 10,
        background: status === 'RUNNING' ? '#F0B42906' : status === 'COMPLETE' ? '#00C85306' : 'transparent',
        border: `1px solid ${status === 'QUEUED' ? '#182030' : color + '25'}`,
        borderRadius: 8, padding: '10px 14px',
        opacity: status === 'QUEUED' ? 0.45 : 1,
        transition: 'all 0.3s',
      }}
    >
      <Icon size={13} style={{ color, flexShrink: 0 }} className={status === 'RUNNING' ? 'animate-pulse' : undefined} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={{ color: status === 'QUEUED' ? '#3A5A7A' : '#DCE8F5', fontSize: 11, fontWeight: 600 }}>{label}</p>
        {summary && (
          <p style={{ color: '#5A7A9A', fontSize: 9, fontFamily: 'monospace', marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {summary}
          </p>
        )}
      </div>
      <span
        style={{
          background: `${color}15`, color, border: `1px solid ${color}25`,
          fontSize: 8, fontFamily: 'monospace', fontWeight: 700,
          padding: '2px 8px', borderRadius: 99, flexShrink: 0,
        }}
      >
        {status === 'RUNNING' ? '● RUNNING' : status}
      </span>
    </div>
  );
}

// ── Live discoveries sidebar ──────────────────────────────────────────────────

function DiscoveriesPanel({ cluster, clientRef }: {
  cluster?: CDDReport['identifier_cluster'];
  clientRef?: string;
}) {
  const variants = cluster ? [...cluster.variants].reverse() : [];
  const seedColors: Record<string, string> = {
    NAME: '#00C8FF', EMAIL: '#2979FF', PHONE: '#00C853', ADDRESS: '#F0B429',
    COMPANY: '#F0B429', USERNAME: '#A855F7', PASSPORT: '#FF6B1A',
    PAN: '#00C853', DL: '#00C853', VOTER_ID: '#00C853',
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <p style={{ color: '#5A7A9A', fontSize: 9, fontFamily: 'monospace', letterSpacing: '0.18em' }}>LIVE DISCOVERIES</p>
        <span style={{ background: '#00C8FF18', color: '#00C8FF', border: '1px solid #00C8FF30', fontSize: 9, fontFamily: 'monospace', fontWeight: 700, padding: '1px 8px', borderRadius: 99 }}>
          {variants.length}
        </span>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, maxHeight: 480, overflowY: 'auto' }}>
        {variants.length === 0 ? (
          <p style={{ color: '#3A5A7A', fontSize: 10, fontFamily: 'monospace', textAlign: 'center', padding: '24px 0' }}>
            Waiting for discoveries…
          </p>
        ) : (
          variants.map((v, i) => {
            const c = seedColors[v.type] ?? '#5A7A9A';
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: 8 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.18, delay: i < 8 ? i * 0.03 : 0 }}
                style={{
                  background: '#0C1118',
                  border: `1px solid ${c}18`,
                  borderLeft: `2px solid ${c}`,
                  borderRadius: 5,
                  padding: '6px 10px',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 2 }}>
                  <span style={{ background: `${c}18`, color: c, fontSize: 8, fontFamily: 'monospace', fontWeight: 700, padding: '1px 5px', borderRadius: 99 }}>
                    {SEED_LABELS[v.type] ?? v.type}
                  </span>
                  {v.source && (
                    <span style={{ color: '#283A50', fontSize: 7, fontFamily: 'monospace' }}>{v.source}</span>
                  )}
                </div>
                <p style={{ color: '#DCE8F5', fontSize: 9, fontFamily: 'monospace', wordBreak: 'break-all' }}>{v.value}</p>
              </motion.div>
            );
          })
        )}
      </div>

      {clientRef && (
        <div style={{ background: '#0C1118', border: '1px solid #182030', borderRadius: 6, padding: '8px 10px' }}>
          <p style={{ color: '#283A50', fontSize: 8, fontFamily: 'monospace', marginBottom: 2 }}>CLIENT REF</p>
          <p style={{ color: '#5A7A9A', fontSize: 9, fontFamily: 'monospace' }}>{clientRef}</p>
        </div>
      )}
    </div>
  );
}

// ── Section header ────────────────────────────────────────────────────────────

function SectionLabel({ label }: { label: string }) {
  return (
    <p style={{ color: '#5A7A9A', fontSize: 9, fontFamily: 'monospace', letterSpacing: '0.15em', marginBottom: 10 }}>
      {label}
    </p>
  );
}

// ── Adverse media ─────────────────────────────────────────────────────────────

const ADVERSE_COLORS: Record<string, string> = {
  CRIMINAL: '#FF1F1F', FRAUD: '#FF6B1A', REGULATORY: '#F0B429',
  SANCTIONS: '#FF1F1F', FINANCIAL_CRIME: '#FF6B1A', ADVERSE: '#F0B429',
  NOT_ADVERSE: '#283A50',
};

function AdverseMediaSection({ articles }: { articles: AdverseArticle[] }) {
  const hits = articles.filter(a => a.category !== 'NOT_ADVERSE');
  if (hits.length === 0) return null;
  return (
    <div>
      <SectionLabel label="ADVERSE MEDIA" />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {hits.slice(0, 8).map((a, i) => {
          const c = ADVERSE_COLORS[a.category ?? 'ADVERSE'] ?? '#F0B429';
          return (
            <div key={i} style={{
              background: '#0C1118', border: '1px solid #182030',
              borderLeft: `3px solid ${c}`, borderRadius: 8, padding: '10px 12px',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                <span style={{
                  background: `${c}18`, color: c, border: `1px solid ${c}30`,
                  fontSize: 8, fontFamily: 'monospace', fontWeight: 700,
                  padding: '1px 6px', borderRadius: 99,
                }}>{a.category ?? 'ADVERSE'}</span>
                {a.source && <span style={{ color: '#283A50', fontSize: 8, fontFamily: 'monospace' }}>{a.source}</span>}
                {a.published_date && <span style={{ color: '#283A50', fontSize: 8, fontFamily: 'monospace', marginLeft: 'auto' }}>{a.published_date}</span>}
              </div>
              <p style={{ color: '#DCE8F5', fontSize: 10, fontWeight: 600, marginBottom: 2 }}>{a.title}</p>
              {a.snippet && <p style={{ color: '#5A7A9A', fontSize: 9, lineHeight: 1.4 }}>{a.snippet}</p>}
            </div>
          );
        })}
        {hits.length > 8 && (
          <p style={{ color: '#283A50', fontSize: 9, fontFamily: 'monospace', textAlign: 'center' }}>
            +{hits.length - 8} more articles
          </p>
        )}
      </div>
    </div>
  );
}

// ── Enforcement actions ───────────────────────────────────────────────────────

const AUTH_COLORS: Record<string, string> = {
  SEBI: '#F0B429', RBI: '#2979FF', ED: '#FF6B1A', NCLT: '#C026D3',
  SFIO: '#FF1F1F', IRDAI: '#00C8FF', PFRDA: '#00C8FF',
};

function EnforcementSection({ hits }: { hits: EnforcementHit[] }) {
  if (!hits.length) return null;
  return (
    <div>
      <SectionLabel label="ENFORCEMENT ACTIONS" />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
        {hits.map((h, i) => {
          const c = AUTH_COLORS[h.authority ?? ''] ?? '#5A7A9A';
          return (
            <div key={i} style={{
              background: '#0C1118', border: '1px solid #182030',
              borderLeft: `3px solid ${c}`, borderRadius: 8, padding: '9px 12px',
              display: 'flex', alignItems: 'flex-start', gap: 10,
            }}>
              <span style={{
                background: `${c}18`, color: c, border: `1px solid ${c}30`,
                fontSize: 8, fontFamily: 'monospace', fontWeight: 700,
                padding: '2px 7px', borderRadius: 99, flexShrink: 0, marginTop: 1,
              }}>{h.authority ?? 'REGULATOR'}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                {h.action_type && <p style={{ color: '#DCE8F5', fontSize: 10, fontWeight: 600 }}>{h.action_type}</p>}
                {h.summary && <p style={{ color: '#5A7A9A', fontSize: 9, marginTop: 2 }}>{h.summary}</p>}
              </div>
              {h.date && <span style={{ color: '#283A50', fontSize: 8, fontFamily: 'monospace', flexShrink: 0 }}>{h.date}</span>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Court cases ───────────────────────────────────────────────────────────────

function CourtCasesSection({ cases }: { cases: CourtCase[] }) {
  if (!cases.length) return null;
  return (
    <div>
      <SectionLabel label="COURT CASES" />
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 6 }}>
        {cases.map((c, i) => (
          <div key={i} style={{
            background: '#0C1118', border: '1px solid #182030',
            borderLeft: '3px solid #C026D3', borderRadius: 8, padding: '9px 12px',
          }}>
            {c.cnr && <p style={{ color: '#C026D3', fontFamily: 'monospace', fontSize: 9, fontWeight: 700 }}>{c.cnr}</p>}
            {c.court && <p style={{ color: '#5A7A9A', fontSize: 8, fontFamily: 'monospace', marginTop: 2 }}>{c.court}</p>}
            {c.parties && <p style={{ color: '#DCE8F5', fontSize: 9, marginTop: 3 }}>{c.parties}</p>}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
              {c.status && (
                <span style={{
                  background: '#182030', color: '#5A7A9A',
                  fontSize: 7, fontFamily: 'monospace', padding: '1px 5px', borderRadius: 4,
                }}>{c.status}</span>
              )}
              {c.filed_date && <span style={{ color: '#283A50', fontSize: 8, fontFamily: 'monospace', marginLeft: 'auto' }}>{c.filed_date}</span>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Social discovery ──────────────────────────────────────────────────────────

const PLATFORM_COLORS: Record<string, string> = {
  twitter: '#1DA1F2', x: '#1DA1F2', instagram: '#E1306C', facebook: '#1877F2',
  linkedin: '#0A66C2', github: '#8B949E', reddit: '#FF4500', telegram: '#26A5E4',
  youtube: '#FF0000', tiktok: '#69C9D0', snapchat: '#FFFC00', pinterest: '#E60023',
};

function SocialSection({ accounts }: { accounts: SocialAccount[] }) {
  // confirmed is set on all returned hits; fall back to confidence>0.5 for older records
  const found = accounts.filter(a => a.confirmed !== false && (a.confirmed || (a.confidence ?? 0) > 0.5));
  if (!accounts.length) return null;
  return (
    <div>
      <SectionLabel label={`SOCIAL DISCOVERY · ${found.length} CONFIRMED / ${accounts.length} CHECKED`} />
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {found.map((a, i) => {
          const c = PLATFORM_COLORS[a.platform.toLowerCase()] ?? '#5A7A9A';
          return (
            <div key={i} style={{
              background: '#0C1118', border: `1px solid ${c}30`,
              borderRadius: 8, padding: '7px 12px',
              display: 'flex', alignItems: 'center', gap: 6,
            }}>
              <span style={{ background: `${c}20`, color: c, fontSize: 8, fontFamily: 'monospace', fontWeight: 700, padding: '1px 6px', borderRadius: 99 }}>
                {a.platform.toUpperCase()}
              </span>
              {a.username && <span style={{ color: '#DCE8F5', fontSize: 10, fontFamily: 'monospace' }}>@{a.username}</span>}
              {a.url && (
                <a href={a.url} target="_blank" rel="noopener noreferrer"
                  style={{ color: '#283A50', fontSize: 8, fontFamily: 'monospace' }}>↗</a>
              )}
            </div>
          );
        })}
        {accounts.length - found.length > 0 && (
          <span style={{ color: '#283A50', fontSize: 9, fontFamily: 'monospace', padding: '7px 0', alignSelf: 'center' }}>
            +{accounts.length - found.length} not found
          </span>
        )}
      </div>
    </div>
  );
}

// ── Commerce footprint ────────────────────────────────────────────────────────

function CommerceSection({ commerce }: { commerce: CommerceFootprint }) {
  if (!commerce.active_commerce_signals && !commerce.upi_handles_verified) return null;
  const tierColor = commerce.lifestyle_tier === 'HIGH' ? '#FF6B1A' : commerce.lifestyle_tier === 'MEDIUM' ? '#F0B429' : '#5A7A9A';
  return (
    <div>
      <SectionLabel label="COMMERCE & PAYMENTS FOOTPRINT" />
      <div style={{ background: '#0C1118', border: '1px solid #182030', borderRadius: 8, padding: '12px 16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
          <span style={{
            background: `${tierColor}18`, color: tierColor, border: `1px solid ${tierColor}30`,
            fontSize: 9, fontFamily: 'monospace', fontWeight: 700, padding: '2px 8px', borderRadius: 99,
          }}>
            {commerce.lifestyle_tier ?? 'LOW'} LIFESTYLE
          </span>
          <span style={{ color: '#5A7A9A', fontSize: 9, fontFamily: 'monospace' }}>
            {commerce.active_commerce_signals ?? 0} active signals
          </span>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
          {commerce.upi_handles_verified !== undefined && (
            <div>
              <p style={{ color: '#5A7A9A', fontSize: 8, fontFamily: 'monospace' }}>UPI HANDLES</p>
              <p style={{ color: '#DCE8F5', fontFamily: 'monospace', fontWeight: 700, fontSize: 14 }}>
                {commerce.upi_handles_verified} verified
              </p>
            </div>
          )}
          {commerce.upi_customer_name && (
            <div>
              <p style={{ color: '#5A7A9A', fontSize: 8, fontFamily: 'monospace' }}>UPI NAME</p>
              <p style={{ color: '#DCE8F5', fontSize: 11, fontWeight: 600 }}>{commerce.upi_customer_name}</p>
            </div>
          )}
          {commerce.primary_upi_app && (
            <div>
              <p style={{ color: '#5A7A9A', fontSize: 8, fontFamily: 'monospace' }}>PRIMARY APP</p>
              <p style={{ color: '#DCE8F5', fontSize: 11, fontWeight: 600 }}>{commerce.primary_upi_app}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── LinkedIn profile ──────────────────────────────────────────────────────────

function LinkedInSection({ profile }: { profile: LinkedInProfile }) {
  if (!profile.full_name || profile.degraded) return null;
  const currentPos = profile.positions?.find(p => p.is_current);
  return (
    <div>
      <SectionLabel label="LINKEDIN PROFESSIONAL PROFILE" />
      <div style={{ background: '#0C1118', border: '1px solid #0A66C230', borderLeft: '3px solid #0A66C2', borderRadius: 8, padding: '14px 16px' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
          <div style={{ width: 38, height: 38, borderRadius: '50%', background: '#0A66C220', border: '1px solid #0A66C240', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <UserCheck size={18} style={{ color: '#0A66C2' }} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={{ color: '#DCE8F5', fontWeight: 700, fontSize: 13 }}>{profile.full_name}</p>
            {profile.headline && <p style={{ color: '#5A7A9A', fontSize: 10, marginTop: 2 }}>{profile.headline}</p>}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginTop: 8 }}>
              {profile.location && (
                <span style={{ display: 'flex', alignItems: 'center', gap: 4, color: '#5A7A9A', fontSize: 9, fontFamily: 'monospace' }}>
                  <MapPin size={9} />{profile.location}
                </span>
              )}
              {profile.follower_count != null && (
                <span style={{ display: 'flex', alignItems: 'center', gap: 4, color: '#5A7A9A', fontSize: 9, fontFamily: 'monospace' }}>
                  <Users size={9} />{profile.follower_count.toLocaleString()} followers
                </span>
              )}
            </div>
          </div>
        </div>
        {currentPos && (
          <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid #182030', display: 'flex', alignItems: 'center', gap: 6 }}>
            <Briefcase size={10} style={{ color: '#5A7A9A' }} />
            <span style={{ color: '#DCE8F5', fontSize: 10 }}>{currentPos.title}</span>
            {currentPos.company && <span style={{ color: '#5A7A9A', fontSize: 10 }}>at {currentPos.company}</span>}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Pivot chip row ────────────────────────────────────────────────────────────

function PivotChips({
  label, variants, color, discardedSet, onDiscard,
}: {
  label: string;
  variants: Array<{ value: string; source?: string }>;
  color: string;
  discardedSet: Set<string>;
  onDiscard: (value: string, undo: boolean) => void;
}) {
  if (!variants.length) return null;
  return (
    <div>
      <SectionLabel label={label} />
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {variants.map((v, i) => {
          const discarded = discardedSet.has(v.value);
          return (
            <div
              key={i}
              className="group"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                background: discarded ? '#080C10' : `${color}12`,
                color: discarded ? '#3A5A7A' : color,
                border: `1px solid ${discarded ? '#182030' : color + '40'}`,
                borderRadius: 6, padding: '5px 10px',
                opacity: discarded ? 0.6 : 1,
                textDecoration: discarded ? 'line-through' : undefined,
              }}
            >
              <span style={{ fontSize: 10, fontFamily: 'monospace', fontWeight: 700 }}>
                {v.value}
              </span>
              <button
                onClick={() => onDiscard(v.value, discarded)}
                className="opacity-0 group-hover:opacity-100 transition-opacity"
                style={{
                  background: discarded ? '#00C85318' : '#FF444418',
                  border: `1px solid ${discarded ? '#00C85330' : '#FF444430'}`,
                  color: discarded ? '#00C853' : '#FF4444',
                  borderRadius: 3, padding: '1px 3px', cursor: 'pointer',
                  display: 'flex', alignItems: 'center',
                }}
              >
                {discarded ? <RotateCcw size={8} /> : <XIcon size={8} />}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── EDD results summary (COMPLETE state) ──────────────────────────────────────

function EDDResultsSummary({
  data, caseId, userRole, discardedSet, onDiscard,
}: {
  data: CDDReport;
  caseId: string;
  userRole?: string;
  discardedSet: Set<string>;
  onDiscard: (value: string, undo: boolean) => void;
}) {
  const navigate = useNavigate();
  const tier = data.risk_tier;
  const tierColor = TIER_COLOR[tier] ?? '#5A7A9A';
  const pipeline = data.pipeline_result;
  const cluster = data.identifier_cluster ?? pipeline?.cluster;

  const [dlLoadingAml, setDlLoadingAml] = useState(false);
  const [dlLoadingDossier, setDlLoadingDossier] = useState(false);
  const canDossier = userRole === 'senior_analyst' || userRole === 'admin';

  async function download(track: 'aml' | 'dossier') {
    if (track === 'aml') setDlLoadingAml(true);
    else setDlLoadingDossier(true);
    try {
      const blob = await profilerApi.downloadReport(caseId, track);
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `sycek_edd_${track}_${data.case_reference}.pdf`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : 'Download failed');
    } finally {
      if (track === 'aml') setDlLoadingAml(false);
      else setDlLoadingDossier(false);
    }
  }

  // Key findings
  const sanctionsHits  = pipeline?.sanctions?.hits?.length ?? 0;
  const adverseHits    = pipeline?.adverse_media?.articles?.filter((a: AdverseArticle) => a.category !== 'NOT_ADVERSE').length ?? 0;
  const breachCount    = pipeline?.ato?.breach_count ?? 0;
  const stealerCount   = pipeline?.ato?.stealer_log_count ?? 0;
  const socialAccounts = pipeline?.social?.accounts?.length ?? 0;
  const companyCount   = pipeline?.corporate?.mca21?.companies_as_director?.length ?? 0;
  const courtCases     = pipeline?.court?.cases?.length ?? 0;
  const identifiers    = cluster?.variants?.length ?? 0;

  // EDD-exclusive data
  const articles      = pipeline?.adverse_media?.articles ?? [];
  const enforcement   = pipeline?.enforcement_hits ?? [];
  const courtList     = pipeline?.court?.cases ?? [];
  const socialList    = pipeline?.social?.accounts ?? [];
  const commerce      = pipeline?.commerce;
  const linkedin      = pipeline?.linkedin;

  // Pivot chain chips
  const allVariants = cluster?.variants ?? [];
  const pivotValidated = allVariants.filter(v =>
    v.source === 'breach_pivot(validated)' || v.source === 'breach_validated'
  );
  const bidirectional = allVariants.filter(v => v.source === 'breach_validated');
  const hasBreach = allVariants.some(v => v.source?.startsWith('breach'));

  type Metric = { label: string; value: string | number; color: string; sub?: string };
  const metrics: Metric[] = [
    { label: 'SANCTIONS HITS',  value: sanctionsHits,  color: sanctionsHits > 0  ? '#FF1F1F' : '#5A7A9A', sub: 'OpenSanctions / OFAC' },
    { label: 'ADVERSE MEDIA',   value: adverseHits,    color: adverseHits > 0    ? '#FF6B1A' : '#5A7A9A', sub: 'Criminal / Fraud / Reg.' },
    { label: 'BREACH SOURCES',  value: breachCount,    color: breachCount > 0    ? '#F0B429' : '#5A7A9A', sub: 'Credential databases' },
    { label: 'STEALER LOGS',    value: stealerCount,   color: stealerCount > 0   ? '#FF6B1A' : '#5A7A9A', sub: 'Dark-web infostealer' },
    { label: 'SOCIAL ACCOUNTS', value: socialAccounts, color: socialAccounts > 0 ? '#2979FF' : '#5A7A9A', sub: 'Cross-platform' },
    { label: 'DIRECTORSHIPS',   value: companyCount,   color: companyCount > 0   ? '#F0B429' : '#5A7A9A', sub: 'MCA21 registry' },
    { label: 'COURT CASES',     value: courtCases,     color: courtCases > 0     ? '#FF6B1A' : '#5A7A9A', sub: 'eCourts India' },
    { label: 'IDENTIFIERS',     value: identifiers,    color: '#00C8FF',                                   sub: 'Cluster size' },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>

      {/* Verdict banner */}
      <motion.div
        initial={{ opacity: 0, scale: 0.97 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.4 }}
        style={{
          background: `${tierColor}12`,
          border: `1px solid ${tierColor}50`,
          borderRadius: 12,
          padding: '20px 24px',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
          <CheckCircle size={22} style={{ color: tierColor }} />
          <div>
            <p style={{ color: tierColor, fontFamily: 'monospace', fontWeight: 900, fontSize: 15, letterSpacing: '0.12em' }}>
              EDD COMPLETE — {tier} RISK
            </p>
            <p style={{ color: '#5A7A9A', fontFamily: 'monospace', fontSize: 9, marginTop: 2 }}>
              {data.case_reference} · {data.seed ?? cluster?.canonical}
            </p>
          </div>
          <div style={{ marginLeft: 'auto' }}>
            <RiskTierBadge tier={tier as RiskTier} pulse={tier === 'CRITICAL'} />
          </div>
        </div>

        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <button
            onClick={() => navigate(`/app/profiler/case/${caseId}/dossier`)}
            style={{
              display: 'flex', alignItems: 'center', gap: 8,
              background: '#00C8FF18', color: '#00C8FF',
              border: '1px solid #00C8FF44',
              borderRadius: 8, padding: '8px 16px',
              fontFamily: 'monospace', fontSize: 10, fontWeight: 700,
              cursor: 'pointer', letterSpacing: '0.08em',
            }}
          >
            VIEW FULL DOSSIER <ArrowRight size={12} />
          </button>
          <button
            onClick={() => download('aml')}
            disabled={dlLoadingAml}
            style={{
              display: 'flex', alignItems: 'center', gap: 8,
              background: '#002733', color: '#00C8FF',
              border: '1px solid #00C8FF44',
              borderRadius: 8, padding: '8px 16px',
              fontFamily: 'monospace', fontSize: 10, fontWeight: 700,
              cursor: dlLoadingAml ? 'not-allowed' : 'pointer', opacity: dlLoadingAml ? 0.6 : 1,
            }}
          >
            {dlLoadingAml ? <Loader2 size={12} className="animate-spin" /> : <FileText size={12} />}
            AML REPORT PDF
          </button>
          {canDossier && (
            <button
              onClick={() => download('dossier')}
              disabled={dlLoadingDossier}
              style={{
                display: 'flex', alignItems: 'center', gap: 8,
                background: '#1A032A', color: '#C026D3',
                border: '1px solid #C026D344',
                borderRadius: 8, padding: '8px 16px',
                fontFamily: 'monospace', fontSize: 10, fontWeight: 700,
                cursor: dlLoadingDossier ? 'not-allowed' : 'pointer', opacity: dlLoadingDossier ? 0.6 : 1,
              }}
            >
              {dlLoadingDossier ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />}
              FULL DOSSIER PDF
            </button>
          )}
        </div>
      </motion.div>

      {/* Key findings grid */}
      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, delay: 0.1 }}>
        <SectionLabel label="KEY FINDINGS" />
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10 }}>
          {metrics.map(m => (
            <div
              key={m.label}
              style={{
                background: '#090D13',
                border: `1px solid ${(typeof m.value === 'number' && m.value > 0) ? m.color + '30' : '#182030'}`,
                borderRadius: 8, padding: '12px 14px',
              }}
            >
              <p style={{ color: m.color, fontFamily: 'monospace', fontWeight: 900, fontSize: 22, lineHeight: 1 }}>{m.value}</p>
              <p style={{ color: '#5A7A9A', fontSize: 8, fontFamily: 'monospace', letterSpacing: '0.06em', marginTop: 4 }}>{m.label}</p>
              {m.sub && <p style={{ color: '#283A50', fontSize: 7, fontFamily: 'monospace', marginTop: 2 }}>{m.sub}</p>}
            </div>
          ))}
        </div>
      </motion.div>

      {/* ── Breach intelligence ──────────────────────────────────────────────── */}
      {hasBreach && cluster && (
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, delay: 0.15 }}
          style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

          <div style={{ borderTop: '1px solid #182030', paddingTop: 20 }}>
            <SectionLabel label="BREACH INTELLIGENCE" />
          </div>

          <PivotChain cluster={cluster} />

          {pivotValidated.length > 0 && (
            <PivotChips
              label="PIVOT-VALIDATED IDENTIFIERS"
              variants={pivotValidated}
              color="#00C853"
              discardedSet={discardedSet}
              onDiscard={onDiscard}
            />
          )}

          {bidirectional.length > 0 && (
            <PivotChips
              label="BIDIRECTIONALLY CONFIRMED"
              variants={bidirectional}
              color="#00C8FF"
              discardedSet={discardedSet}
              onDiscard={onDiscard}
            />
          )}

          <IdentifierClusterGrid cluster={cluster} onDiscard={onDiscard} />
        </motion.div>
      )}

      {/* ── EDD-exclusive findings ──────────────────────────────────────────── */}
      {(articles.length > 0 || enforcement.length > 0 || courtList.length > 0 || socialList.length > 0 || commerce || linkedin) && (
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, delay: 0.2 }}
          style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          <div style={{ borderTop: '1px solid #182030', paddingTop: 20 }}>
            <SectionLabel label="EDD EXCLUSIVE FINDINGS" />
          </div>
          <AdverseMediaSection articles={articles} />
          <EnforcementSection hits={enforcement} />
          <CourtCasesSection cases={courtList} />
          <SocialSection accounts={socialList} />
          {commerce && <CommerceSection commerce={commerce} />}
          {linkedin && <LinkedInSection profile={linkedin} />}
        </motion.div>
      )}

      {/* Module results checklist */}
      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, delay: 0.25 }}>
        <SectionLabel label="MODULE RESULTS" />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {PIPELINE_ORDER.map((mod, i) => {
            const cr = data.check_results?.find(r => r.module === mod);
            return (
              <motion.div key={mod} initial={{ opacity: 0, x: -6 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.2, delay: i * 0.04 }}>
                <ModuleRow module={mod} status={cr?.status ?? 'COMPLETE'} summary={cr?.result_summary} />
              </motion.div>
            );
          })}
        </div>
      </motion.div>
    </div>
  );
}

// ── Running pipeline view ─────────────────────────────────────────────────────

function RunningPipelineView({ data, caseId }: { data: CDDReport; caseId: string }) {
  const navigate = useNavigate();
  const elapsed = useElapsedSeconds(data.created_at);
  const completedCount = countCompleted(data.check_results);
  const pct = Math.round((completedCount / TOTAL_MODULES) * 100);
  const pipelineColor = '#F0B429';
  const currentMod = activeModule(data.check_results);
  const currentModLabel = currentMod ? (MODULE_LABELS[currentMod] ?? currentMod) : null;

  return (
    <div style={{ display: 'flex', gap: 20 }}>
      {/* Left: modules */}
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 16 }}>
        {/* Status header */}
        <div
          style={{
            background: '#090D13',
            border: '1px solid #F0B42930',
            borderRadius: 12,
            padding: '16px 20px',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
            <span className="relative flex h-3 w-3 flex-shrink-0">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75" style={{ background: '#F0B429' }} />
              <span className="relative inline-flex rounded-full h-3 w-3" style={{ background: '#F0B429' }} />
            </span>
            <div style={{ flex: 1 }}>
              <p style={{ color: '#DCE8F5', fontFamily: 'monospace', fontWeight: 700, fontSize: 13, letterSpacing: '0.1em' }}>
                EDD PIPELINE — RUNNING
              </p>
              {currentModLabel && (
                <p style={{ color: '#F0B429', fontFamily: 'monospace', fontSize: 9, marginTop: 2 }}>
                  ● {currentModLabel.toUpperCase()}
                </p>
              )}
            </div>
            <div style={{ textAlign: 'right' }}>
              <p style={{ color: pipelineColor, fontFamily: 'monospace', fontWeight: 900, fontSize: 18 }}>
                {completedCount}/{TOTAL_MODULES}
              </p>
              <p style={{ color: '#5A7A9A', fontFamily: 'monospace', fontSize: 8 }}>MODULES</p>
            </div>
          </div>

          {/* Progress bar */}
          <div style={{ height: 3, background: '#182030', borderRadius: 3, overflow: 'hidden' }}>
            <motion.div
              animate={{ width: `${pct}%` }}
              transition={{ duration: 0.6, ease: 'easeOut' }}
              style={{ height: '100%', background: 'linear-gradient(90deg, #F0B429, #FF6B1A)', borderRadius: 3 }}
            />
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6 }}>
            <span style={{ color: '#3A5A7A', fontFamily: 'monospace', fontSize: 8 }}>ELAPSED: {fmt(elapsed)}</span>
            <span style={{ color: pipelineColor, fontFamily: 'monospace', fontSize: 8 }}>{pct}%</span>
          </div>
        </div>

        {/* Module rows */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
          {PIPELINE_ORDER.map((mod, idx) => {
            const status = resolveStatus(mod, data.check_results);
            const cr = data.check_results?.find(r => r.module === mod);
            return (
              <motion.div
                key={mod}
                initial={{ opacity: 0, y: 5 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.2, delay: idx * 0.035 }}
              >
                <ModuleRow module={mod} status={status} summary={cr?.result_summary} />
              </motion.div>
            );
          })}
        </div>

        {/* Action bar */}
        <div
          style={{
            background: '#090D13', border: '1px solid #182030',
            borderRadius: 10, padding: '12px 16px',
            display: 'flex', gap: 10, alignItems: 'center',
          }}
        >
          <button
            onClick={() => navigate(`/app/profiler/case/${caseId}/dossier`)}
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              background: '#00C8FF18', color: '#00C8FF',
              border: '1px solid #00C8FF44',
              borderRadius: 6, padding: '6px 14px',
              fontFamily: 'monospace', fontSize: 9, fontWeight: 700, cursor: 'pointer',
            }}
          >
            VIEW PARTIAL RESULTS <ArrowRight size={11} />
          </button>
          <button
            disabled
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              color: '#FF1F1F', border: '1px solid #FF1F1F30',
              borderRadius: 6, padding: '6px 14px',
              fontFamily: 'monospace', fontSize: 9, fontWeight: 700,
              background: 'transparent', opacity: 0.35, cursor: 'not-allowed',
            }}
          >
            <Ban size={11} /> CANCEL JOB
          </button>
        </div>
      </div>

      {/* Right: discoveries */}
      <div style={{ width: 280, flexShrink: 0 }}>
        <motion.div
          initial={{ opacity: 0, x: 12 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.3, delay: 0.1 }}
          style={{
            background: '#090D13', border: '1px solid #182030',
            borderRadius: 12, padding: 16, position: 'sticky', top: 16,
          }}
        >
          <DiscoveriesPanel cluster={data.identifier_cluster} clientRef={data.client_ref} />
        </motion.div>
      </div>
    </div>
  );
}

// ── Failed view ───────────────────────────────────────────────────────────────

function FailedView({ caseId }: { caseId: string }) {
  const navigate = useNavigate();
  return (
    <div
      style={{
        background: '#0C0508', border: '1px solid #FF1F1F30',
        borderRadius: 12, padding: '28px 24px',
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12,
      }}
    >
      <XCircle size={36} style={{ color: '#FF1F1F' }} />
      <p style={{ color: '#FF1F1F', fontFamily: 'monospace', fontWeight: 700, fontSize: 14, letterSpacing: '0.1em' }}>
        PIPELINE FAILED
      </p>
      <p style={{ color: '#5A7A9A', fontFamily: 'monospace', fontSize: 10, textAlign: 'center', maxWidth: 360 }}>
        One or more critical modules failed to complete. Partial results may still be available in the dossier.
      </p>
      <button
        onClick={() => navigate(`/app/profiler/case/${caseId}/dossier`)}
        style={{
          display: 'flex', alignItems: 'center', gap: 8,
          background: '#FF1F1F18', color: '#FF1F1F',
          border: '1px solid #FF1F1F44',
          borderRadius: 8, padding: '8px 16px',
          fontFamily: 'monospace', fontSize: 10, fontWeight: 700, cursor: 'pointer',
        }}
      >
        VIEW PARTIAL RESULTS <ArrowRight size={12} />
      </button>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function EDDPipeline() {
  const { caseId } = useParams<{ caseId: string }>();
  const navigate   = useNavigate();
  const { user }   = useAuth();
  const userRole   = user?.role;

  const { data, isLoading, isError, sessionExpired } = useEDDPoller(caseId);
  const status = data?.status ?? 'PENDING';

  const [localDiscarded, setLocalDiscarded] = useState<Set<string>>(new Set());
  const serverDiscarded = new Set(data?.identifier_cluster?.discarded_variants ?? []);
  const discardedSet = new Set([...serverDiscarded, ...localDiscarded]);

  const handleDiscard = async (value: string, undo: boolean) => {
    setLocalDiscarded(prev => {
      const next = new Set(prev);
      if (undo) next.delete(value); else next.add(value);
      return next;
    });
    if (!caseId) return;
    try {
      await profilerApi.discardVariant(caseId, value, undo);
    } catch {
      setLocalDiscarded(prev => {
        const next = new Set(prev);
        if (undo) next.add(value); else next.delete(value);
        return next;
      });
    }
  };

  const elapsed      = useElapsedSeconds(data?.created_at);
  const completed    = countCompleted(data?.check_results);
  const currentMod   = activeModule(data?.check_results);
  const discoveries  = data?.identifier_cluster?.variants?.length ?? 0;
  const entityName   = data?.seed ?? data?.identifier_cluster?.canonical ?? '';

  // ── Session expired (during long EDD polling)
  if (sessionExpired) {
    return (
      <div style={{ background: '#050709', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ background: '#090D13', border: '1px solid #F0B42930', borderRadius: 12, padding: 32, textAlign: 'center', maxWidth: 380 }}>
          <p style={{ color: '#F0B429', fontFamily: 'monospace', fontWeight: 700, fontSize: 13, marginBottom: 8, letterSpacing: '0.1em' }}>
            SESSION EXPIRED
          </p>
          <p style={{ color: '#5A7A9A', fontFamily: 'monospace', fontSize: 10, marginBottom: 6 }}>
            Your session timed out while the EDD pipeline was running.
          </p>
          <p style={{ color: '#3A5A7A', fontFamily: 'monospace', fontSize: 9, marginBottom: 20 }}>
            Case {caseId} is still processing in the background. Re-authenticate and return to this page.
          </p>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
            <button
              onClick={() => { localStorage.removeItem('auth_token'); navigate('/login'); }}
              style={{ background: '#F0B42918', color: '#F0B429', border: '1px solid #F0B42944', borderRadius: 8, padding: '8px 16px', fontFamily: 'monospace', fontSize: 10, fontWeight: 700, cursor: 'pointer' }}
            >
              RE-AUTHENTICATE
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Error state
  if (isError || (!isLoading && !data)) {
    return (
      <div style={{ background: '#050709', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ background: '#090D13', border: '1px solid #FF1F1F30', borderRadius: 12, padding: 32, textAlign: 'center', maxWidth: 360 }}>
          <XCircle size={32} style={{ color: '#FF1F1F', margin: '0 auto 12px' }} />
          <p style={{ color: '#DCE8F5', fontFamily: 'monospace', fontWeight: 700, fontSize: 13, marginBottom: 6 }}>Pipeline Not Found</p>
          <p style={{ color: '#5A7A9A', fontFamily: 'monospace', fontSize: 10, marginBottom: 16 }}>Case {caseId} could not be loaded.</p>
          <button onClick={() => navigate('/app/profiler')} style={{ background: '#A855F718', color: '#A855F7', border: '1px solid #A855F744', borderRadius: 8, padding: '8px 16px', fontFamily: 'monospace', fontSize: 10, fontWeight: 700, cursor: 'pointer' }}>
            BACK TO PROFILER
          </button>
        </div>
      </div>
    );
  }

  // ── PENDING → cinematic scan loader (RUNNING always shows pipeline view)
  if (!data || status === 'PENDING') {
    return (
      <ScanLoader
        entityName={entityName}
        elapsed={elapsed}
        completedCount={completed}
        total={TOTAL_MODULES}
        currentModule={currentMod}
        discoveries={discoveries}
      />
    );
  }

  return (
    <div style={{ background: '#050709', minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* Nav bar */}
      <div style={{ background: '#090D13', borderBottom: '1px solid #182030' }}>
        <div className="max-w-6xl mx-auto px-6 py-3 flex items-center gap-3">
          <button onClick={() => navigate('/app/profiler')} style={{ color: '#5A7A9A', fontFamily: 'monospace', fontSize: 10, background: 'none', border: 'none', cursor: 'pointer' }}>
            PROFILER
          </button>
          <ChevronRight size={10} style={{ color: '#3A5A7A' }} />
          <span style={{ color: '#DCE8F5', fontFamily: 'monospace', fontSize: 10 }}>EDD PIPELINE</span>
          <span style={{ marginLeft: 'auto', color: '#3A5A7A', fontFamily: 'monospace', fontSize: 10 }}>{data?.case_reference}</span>
        </div>
      </div>

      <div className="flex-1 max-w-6xl mx-auto w-full px-6 py-6">
        <AnimatePresence mode="wait">
          {status === 'RUNNING' && (
            <motion.div key="running" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <RunningPipelineView data={data} caseId={caseId!} />
            </motion.div>
          )}

          {status === 'COMPLETE' && (
            <motion.div key="complete" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
              <EDDResultsSummary
                data={data}
                caseId={caseId!}
                userRole={userRole}
                discardedSet={discardedSet}
                onDiscard={handleDiscard}
              />
            </motion.div>
          )}

          {status === 'FAILED' && (
            <motion.div key="failed" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <FailedView caseId={caseId!} />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
